import React, { useState, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { useTranslation } from '../lib/i18n';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { 
  Upload, FileSpreadsheet, FileText, CheckCircle2, 
  AlertCircle, ArrowRight, Download, Trash2, Eye,
  Sparkles, RefreshCw, X, ChevronDown, ChevronUp
} from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '../lib/utils';
import { accountsAPI, categoriesAPI } from '../lib/api';
import api from '../lib/api';

const BankImport = () => {
  const { user, language } = useAuth();
  const t = useTranslation(language);
  
  const [file, setFile] = useState(null);
  const [fileType, setFileType] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [parsing, setParsing] = useState(false);
  const [importing, setImporting] = useState(false);
  const [progress, setProgress] = useState(0);
  const [parsedData, setParsedData] = useState(null);
  const [selectedAccount, setSelectedAccount] = useState('');
  const [accounts, setAccounts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedTransactions, setSelectedTransactions] = useState([]);
  const [showPreview, setShowPreview] = useState(true);
  const [importResult, setImportResult] = useState(null);

  // Cargar cuentas y categorías
  React.useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [accountsRes, categoriesRes] = await Promise.all([
        accountsAPI.getAll(),
        categoriesAPI.getAll()
      ]);
      setAccounts(accountsRes.data);
      setCategories(categoriesRes.data);
      if (accountsRes.data.length > 0) {
        setSelectedAccount(accountsRes.data[0].id);
      }
    } catch (error) {
      toast.error('Error al cargar cuentas y categorías');
    }
  };

  const supportedFormats = [
    { 
      type: 'csv', 
      name: 'CSV', 
      icon: FileSpreadsheet, 
      description: 'Archivo separado por comas',
      banks: ['Santander', 'BBVA', 'CaixaBank', 'ING', 'N26', 'Revolut', 'Genérico']
    },
    { 
      type: 'ofx', 
      name: 'OFX/QFX', 
      icon: FileText, 
      description: 'Open Financial Exchange',
      banks: ['La mayoría de bancos', 'Quicken', 'Money']
    },
  ];

  const handleFileSelect = useCallback((e) => {
    const selectedFile = e.target.files[0];
    if (!selectedFile) return;

    const extension = selectedFile.name.split('.').pop().toLowerCase();
    
    if (!['csv', 'ofx', 'qfx'].includes(extension)) {
      toast.error('Formato no soportado. Usa CSV, OFX o QFX.');
      return;
    }

    setFile(selectedFile);
    setFileType(extension === 'qfx' ? 'ofx' : extension);
    setParsedData(null);
    setImportResult(null);
    setSelectedTransactions([]);
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    const droppedFile = e.dataTransfer.files[0];
    if (droppedFile) {
      const fakeEvent = { target: { files: [droppedFile] } };
      handleFileSelect(fakeEvent);
    }
  }, [handleFileSelect]);

  const parseFile = async () => {
    if (!file || !selectedAccount) {
      toast.error('Selecciona un archivo y una cuenta');
      return;
    }

    setParsing(true);
    setProgress(0);

    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('account_id', selectedAccount);
      formData.append('file_type', fileType);

      // Simular progreso
      const progressInterval = setInterval(() => {
        setProgress(prev => Math.min(prev + 10, 90));
      }, 200);

      const response = await api.post('/import/parse', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      clearInterval(progressInterval);
      setProgress(100);

      const data = response.data;
      setParsedData(data);
      setSelectedTransactions(data.transactions.map((_, i) => i));
      
      toast.success(`${data.transactions.length} transacciones encontradas`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al procesar el archivo');
    } finally {
      setParsing(false);
    }
  };

  const handleImport = async () => {
    if (!parsedData || selectedTransactions.length === 0) {
      toast.error('No hay transacciones seleccionadas');
      return;
    }

    setImporting(true);
    setProgress(0);

    try {
      const transactionsToImport = parsedData.transactions.filter((_, i) => 
        selectedTransactions.includes(i)
      );

      const response = await api.post('/import/confirm', {
        account_id: selectedAccount,
        transactions: transactionsToImport
      });

      setImportResult(response.data);
      toast.success(`${response.data.imported} transacciones importadas correctamente`);
      
      // Reset state
      setFile(null);
      setParsedData(null);
      setSelectedTransactions([]);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al importar');
    } finally {
      setImporting(false);
    }
  };

  const toggleTransaction = (index) => {
    setSelectedTransactions(prev => 
      prev.includes(index) 
        ? prev.filter(i => i !== index)
        : [...prev, index]
    );
  };

  const toggleAll = () => {
    if (selectedTransactions.length === parsedData?.transactions.length) {
      setSelectedTransactions([]);
    } else {
      setSelectedTransactions(parsedData.transactions.map((_, i) => i));
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('es-ES', {
      style: 'currency',
      currency: user?.currency || 'EUR'
    }).format(amount);
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('es-ES', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  return (
    <div className="space-y-6 pb-8" data-testid="bank-import-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground flex items-center gap-2">
            <Upload className="text-primary" />
            Importar Extracto Bancario
          </h1>
          <p className="text-muted-foreground text-sm mt-1">
            Importa transacciones desde tu banco en segundos
          </p>
        </div>
      </div>

      {/* Import Result */}
      {importResult && (
        <Card className="border-emerald-200 bg-emerald-50 dark:bg-emerald-950/20 dark:border-emerald-800">
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-full bg-emerald-100 dark:bg-emerald-900 flex items-center justify-center">
                <CheckCircle2 className="w-6 h-6 text-emerald-600" />
              </div>
              <div>
                <h3 className="font-semibold text-emerald-800 dark:text-emerald-200">
                  Importación completada
                </h3>
                <p className="text-sm text-emerald-600 dark:text-emerald-400">
                  {importResult.imported} transacciones importadas
                  {importResult.duplicates > 0 && ` • ${importResult.duplicates} duplicadas omitidas`}
                </p>
              </div>
              <Button 
                variant="outline" 
                className="ml-auto"
                onClick={() => setImportResult(null)}
              >
                Nueva importación
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      <div className="grid md:grid-cols-2 gap-6">
        {/* Left: Upload Section */}
        <div className="space-y-4">
          {/* Supported Formats */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-lg">Formatos soportados</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {supportedFormats.map((format) => {
                const Icon = format.icon;
                return (
                  <div 
                    key={format.type}
                    className={cn(
                      "flex items-center gap-3 p-3 rounded-lg border transition-colors",
                      fileType === format.type 
                        ? "border-primary bg-primary/5" 
                        : "border-border hover:bg-muted/50"
                    )}
                  >
                    <Icon className={cn(
                      "w-8 h-8",
                      fileType === format.type ? "text-primary" : "text-muted-foreground"
                    )} />
                    <div className="flex-1">
                      <p className="font-medium text-foreground">{format.name}</p>
                      <p className="text-xs text-muted-foreground">{format.description}</p>
                    </div>
                    <Badge variant="outline" className="text-xs">
                      {format.banks.slice(0, 2).join(', ')}...
                    </Badge>
                  </div>
                );
              })}
            </CardContent>
          </Card>

          {/* Account Selection */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-lg">Cuenta destino</CardTitle>
              <CardDescription>
                Las transacciones se importarán a esta cuenta
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Select value={selectedAccount} onValueChange={setSelectedAccount}>
                <SelectTrigger data-testid="account-select">
                  <SelectValue placeholder="Selecciona cuenta" />
                </SelectTrigger>
                <SelectContent>
                  {accounts.map(account => (
                    <SelectItem key={account.id} value={account.id}>
                      {account.name} ({formatCurrency(account.balance)})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </CardContent>
          </Card>

          {/* File Upload */}
          <Card>
            <CardContent className="pt-6">
              <div
                className={cn(
                  "border-2 border-dashed rounded-lg p-4 sm:p-8 text-center transition-colors",
                  file ? "border-primary bg-primary/5" : "border-border hover:border-primary/50"
                )}
                onDrop={handleDrop}
                onDragOver={(e) => e.preventDefault()}
              >
                {file ? (
                  <div className="space-y-3">
                    <div className="w-12 h-12 rounded-full bg-primary/10 mx-auto flex items-center justify-center">
                      {fileType === 'csv' ? (
                        <FileSpreadsheet className="w-6 h-6 text-primary" />
                      ) : (
                        <FileText className="w-6 h-6 text-primary" />
                      )}
                    </div>
                    <div>
                      <p className="font-medium text-foreground">{file.name}</p>
                      <p className="text-sm text-muted-foreground">
                        {(file.size / 1024).toFixed(1)} KB • {fileType.toUpperCase()}
                      </p>
                    </div>
                    <div className="flex gap-2 justify-center">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          setFile(null);
                          setParsedData(null);
                        }}
                      >
                        <Trash2 size={14} className="mr-1" />
                        Eliminar
                      </Button>
                      <Button
                        size="sm"
                        onClick={parseFile}
                        disabled={parsing || !selectedAccount}
                        data-testid="parse-file-btn"
                      >
                        {parsing ? (
                          <>
                            <RefreshCw size={14} className="mr-1 animate-spin" />
                            Procesando...
                          </>
                        ) : (
                          <>
                            <Sparkles size={14} className="mr-1" />
                            Analizar archivo
                          </>
                        )}
                      </Button>
                    </div>
                  </div>
                ) : (
                  <label className="cursor-pointer block">
                    <input
                      type="file"
                      accept=".csv,.ofx,.qfx"
                      onChange={handleFileSelect}
                      className="hidden"
                      data-testid="file-input"
                    />
                    <Upload className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
                    <p className="font-medium text-foreground">
                      Arrastra un archivo o haz clic para seleccionar
                    </p>
                    <p className="text-sm text-muted-foreground mt-1">
                      CSV, OFX o QFX hasta 10MB
                    </p>
                  </label>
                )}
              </div>

              {parsing && (
                <div className="mt-4">
                  <Progress value={progress} className="h-2" />
                  <p className="text-xs text-muted-foreground mt-1 text-center">
                    Analizando transacciones...
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Right: Preview Section */}
        <div className="space-y-4">
          {parsedData ? (
            <Card>
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="text-lg flex items-center gap-2">
                      <Eye size={18} />
                      Vista previa
                      <Badge variant="outline">
                        {selectedTransactions.length}/{parsedData.transactions.length}
                      </Badge>
                    </CardTitle>
                    <CardDescription>
                      Selecciona las transacciones a importar
                    </CardDescription>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setShowPreview(!showPreview)}
                  >
                    {showPreview ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                  </Button>
                </div>
              </CardHeader>

              {showPreview && (
                <CardContent className="space-y-4">
                  {/* Summary */}
                  <div className="grid grid-cols-3 gap-3 p-3 bg-muted/50 rounded-lg">
                    <div className="text-center">
                      <p className="text-xs text-muted-foreground">Ingresos</p>
                      <p className="font-semibold text-emerald-600">
                        +{formatCurrency(parsedData.summary.total_income)}
                      </p>
                    </div>
                    <div className="text-center">
                      <p className="text-xs text-muted-foreground">Gastos</p>
                      <p className="font-semibold text-red-600">
                        {formatCurrency(parsedData.summary.total_expense)}
                      </p>
                    </div>
                    <div className="text-center">
                      <p className="text-xs text-muted-foreground">Balance</p>
                      <p className={cn(
                        "font-semibold",
                        parsedData.summary.balance >= 0 ? "text-emerald-600" : "text-red-600"
                      )}>
                        {formatCurrency(parsedData.summary.balance)}
                      </p>
                    </div>
                  </div>

                  {/* Select All */}
                  <div className="flex items-center gap-2 pb-2 border-b">
                    <Checkbox 
                      checked={selectedTransactions.length === parsedData.transactions.length}
                      onCheckedChange={toggleAll}
                      id="select-all"
                    />
                    <Label htmlFor="select-all" className="text-sm cursor-pointer">
                      Seleccionar todas
                    </Label>
                  </div>

                  {/* Transactions List */}
                  <div className="max-h-[300px] sm:max-h-[400px] overflow-y-auto space-y-2">
                    {parsedData.transactions.map((tx, index) => (
                      <div
                        key={index}
                        className={cn(
                          "flex items-center gap-3 p-3 rounded-lg border transition-colors cursor-pointer",
                          selectedTransactions.includes(index)
                            ? "border-primary bg-primary/5"
                            : "border-border hover:bg-muted/50",
                          tx.is_duplicate && "opacity-50"
                        )}
                        onClick={() => toggleTransaction(index)}
                      >
                        <Checkbox 
                          checked={selectedTransactions.includes(index)}
                          onCheckedChange={() => toggleTransaction(index)}
                        />
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-foreground text-sm truncate">
                            {tx.description}
                          </p>
                          <p className="text-xs text-muted-foreground flex items-center gap-2">
                            {formatDate(tx.date)}
                            {tx.suggested_category && (
                              <Badge variant="outline" className="text-xs">
                                <Sparkles size={10} className="mr-1" />
                                {tx.suggested_category}
                              </Badge>
                            )}
                            {tx.is_duplicate && (
                              <Badge variant="destructive" className="text-xs">
                                Duplicada
                              </Badge>
                            )}
                          </p>
                        </div>
                        <span className={cn(
                          "font-semibold whitespace-nowrap",
                          tx.amount >= 0 ? "text-emerald-600" : "text-red-600"
                        )}>
                          {tx.amount >= 0 ? '+' : ''}{formatCurrency(tx.amount)}
                        </span>
                      </div>
                    ))}
                  </div>

                  {/* Import Button */}
                  <Button 
                    className="w-full"
                    size="lg"
                    onClick={handleImport}
                    disabled={importing || selectedTransactions.length === 0}
                    data-testid="import-btn"
                  >
                    {importing ? (
                      <>
                        <RefreshCw size={16} className="mr-2 animate-spin" />
                        Importando...
                      </>
                    ) : (
                      <>
                        <Download size={16} className="mr-2" />
                        Importar {selectedTransactions.length} transacciones
                      </>
                    )}
                  </Button>
                </CardContent>
              )}
            </Card>
          ) : (
            <Card className="h-full min-h-[400px] flex items-center justify-center">
              <CardContent className="text-center">
                <FileSpreadsheet className="w-16 h-16 text-muted-foreground/50 mx-auto mb-4" />
                <h3 className="font-medium text-foreground mb-2">
                  Vista previa de transacciones
                </h3>
                <p className="text-sm text-muted-foreground max-w-xs">
                  Sube un archivo CSV u OFX para ver las transacciones antes de importarlas
                </p>
              </CardContent>
            </Card>
          )}
        </div>
      </div>

      {/* Help Section */}
      <Card className="bg-muted/30">
        <CardContent className="pt-6">
          <h3 className="font-semibold text-foreground mb-3 flex items-center gap-2">
            <AlertCircle size={18} className="text-amber-500" />
            ¿Cómo exportar desde tu banco?
          </h3>
          <div className="grid sm:grid-cols-2 md:grid-cols-4 gap-4 text-sm">
            <div>
              <p className="font-medium text-foreground">Santander</p>
              <p className="text-muted-foreground">Mis Cuentas → Movimientos → Exportar CSV</p>
            </div>
            <div>
              <p className="font-medium text-foreground">BBVA</p>
              <p className="text-muted-foreground">Posición Global → Movimientos → Descargar</p>
            </div>
            <div>
              <p className="font-medium text-foreground">CaixaBank</p>
              <p className="text-muted-foreground">Cuentas → Extracto → Exportar</p>
            </div>
            <div>
              <p className="font-medium text-foreground">ING/N26/Revolut</p>
              <p className="text-muted-foreground">Ajustes → Exportar movimientos</p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default BankImport;
